/**
 * MarketBrewer LLC - Header Component
 *
 * The design kit's shell header (marketbrewer-design GUIDELINES.md §6): a flat raised band under
 * the metro stripe, the wordmark with an eyebrow under it, nav tabs marked with aria-current, and
 * two outline buttons, so each page's hero keeps the one orange button. Sticky from `sm`; on a
 * phone it scrolls away. Below `xl` the links move into a drawer (a focus-trapped dialog).
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import LanguageSwitcher from "@/components/Navigation/LanguageSwitcher";
import Container from "@/components/Shared/Container";
import { navData } from "@/components/Navigation/navData";
import { ServicesMegaMenu } from "@/components/Navigation/MegaMenu";
import { Wordmark } from "@/design/react/Wordmark";
import { CONTACT } from "@/constants";

interface HeaderProps {
  language: "en" | "es";
}

const Header: React.FC<HeaderProps> = ({ language }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<"services" | "industries" | null>(null);
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const nav = navData[language];

  // Refs for focus management — A11y-grade dialog requires moving focus into the
  // menu on open, trapping Tab inside while open, and returning focus to the
  // trigger on close.
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Where focus goes when the menu closes: back to the menu button, or to the new page's content
  // after following a link. Nothing on page load (the button used to take focus on every load).
  const returnFocusRef = useRef<"toggle" | "main" | null>(null);

  const ctaText =
    language === "en" ? "Set up a 15-min call" : "Agendar llamada de 15 min";
  const ctaPath = language === "en" ? "/book" : "/es/agendar";
  const hireCtaText = language === "en" ? "Hire Me" : "Contrátame";
  const hireCtaPath = language === "en" ? "/hire-me" : "/es/contratame";

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);

  // Collapse accordions when the menu closes so it always reopens clean
  useEffect(() => {
    if (!mobileMenuOpen) setOpenSection(null);
  }, [mobileMenuOpen]);

  // Keyboard handler — Escape closes; Tab is trapped inside the open menu so
  // keyboard users can't accidentally tab into the (visually hidden) page
  // behind the modal.
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setMobileMenuOpen(false);
      return;
    }
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      returnFocusRef.current = "toggle";
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      // Move focus into the panel after the open animation kicks off so screen
      // readers announce the dialog rather than read the still-focused trigger.
      const t = setTimeout(() => closeButtonRef.current?.focus(), 50);
      return () => {
        clearTimeout(t);
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
    // On close, keyboard users land where they were, or at the top of the page they chose (the
    // link they used is gone with the drawer, and focus would otherwise fall to the body).
    if (returnFocusRef.current === "toggle") toggleButtonRef.current?.focus();
    if (returnFocusRef.current === "main") document.getElementById("main-content")?.focus({ preventScroll: true });
    returnFocusRef.current = null;
    return undefined;
  }, [mobileMenuOpen, handleKeyDown]);

  // The drawer exists only below xl: a tablet turned to landscape with it open must not keep the
  // page locked under a menu it can no longer see.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const wide = window.matchMedia("(min-width: 1280px)");
    const onChange = () => {
      if (!wide.matches) return;
      returnFocusRef.current = null;
      setMobileMenuOpen(false);
    };
    wide.addEventListener("change", onChange);
    return () => wide.removeEventListener("change", onChange);
  }, [mobileMenuOpen]);

  // Live URLs end in a slash ("/es/", "/about/") and nav targets do not, so compare without it.
  const trimSlash = (p: string) => p.replace(/\/+$/, "") || "/";
  const here = trimSlash(location.pathname);

  // A desktop tab stays active on the pages under it.
  const isActiveLink = (path: string): boolean => {
    const target = trimSlash(path);
    if (target === "/" || target === "/es") return here === target;
    return here === target || here.startsWith(`${target}/`);
  };

  // The drawer marks only the page itself: "View all services" must not light up on every service page.
  const isCurrentPage = (path: string): boolean => here === trimSlash(path);

  // The drawer opens on the section that holds the current page, so its orange item shows.
  const currentSection = (): "services" | "industries" | null => {
    const { services } = nav;
    const serviceLinks = [services.viewAll, ...services.groups.flatMap((group) => group.links)];
    if (serviceLinks.some((link) => isCurrentPage(link.to))) return "services";
    const industryLinks = [services.industries.viewAll, ...services.industries.links];
    if (industryLinks.some((link) => isCurrentPage(link.to))) return "industries";
    return null;
  };

  const navLink = (item: { label: string; to: string }) => (
    <li>
      <Link
        to={item.to}
        className="nav-link whitespace-nowrap"
        aria-current={isActiveLink(item.to) ? "page" : undefined}
      >
        {item.label}
      </Link>
    </li>
  );

  const close = () => setMobileMenuOpen(false);
  // A link to another page lands the visitor there, so focus goes to that page, not the menu button.
  const followLink = (to: string) => () => {
    if (!isCurrentPage(to)) returnFocusRef.current = "main";
    close();
  };
  const openMenu = () => {
    setOpenSection(currentSection());
    setMobileMenuOpen(true);
  };

  // A top-level drawer item: big, plain, the current page in the text orange.
  const drawerLinkClass = (to: string) =>
    `block py-3 text-2xl font-bold tracking-tight transition-colors rounded-md ${
      isCurrentPage(to) ? "text-accent-text" : "text-primary hover:text-accent-text"
    }`;

  // py-2.5 on a text-base line is the 2.75rem phone tap target (GUIDELINES.md §11).
  const drawerSubLinkClass = (to: string) =>
    `block py-2.5 text-base font-medium transition-colors rounded-md ${
      isCurrentPage(to) ? "text-accent-text" : "text-secondary hover:text-primary"
    }`;

  // The Hire Me button marks its own page the way the nav marks the others: orange words and edge.
  const currentButton = "aria-[current=page]:border-accent-text aria-[current=page]:text-accent-text";

  const chevron = (open: boolean) => (
    <svg
      className={`w-5 h-5 text-muted transition-transform duration-150 ${open ? "rotate-180" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );

  // Opening and closing a section is a state change, so it moves (briefly); nothing enters on load.
  const accordion = (id: string, open: boolean, children: React.ReactNode) => (
    <AnimatePresence initial={false}>
      {open && (
        <m.div
          id={id}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-3">{children}</div>
        </m.div>
      )}
    </AnimatePresence>
  );

  const arrow = (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );

  return (
    <>
      <header
        className="relative top-0 z-50 border-b sm:sticky bg-surface-raised border-border"
        data-print="hide"
      >
        {/* Room for the kit's fixed metro stripe (PageLayout), so it never covers the nav. */}
        <div className="h-1" aria-hidden="true" />

        <Container size="xl">
          <nav className="flex items-center justify-between h-16 lg:h-20">
            <Link to={language === "en" ? "/" : "/es"} className="flex flex-col group shrink-0">
              <Wordmark className="text-xl lg:text-2xl" />
              <span className="eyebrow -mt-0.5 whitespace-nowrap text-[0.625rem] lg:text-xs">
                Marketing · Web · SEO · Social
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="items-center hidden gap-5 xl:flex">
              <ul className="flex items-center">
                {navLink(nav.home)}
                <ServicesMegaMenu language={language} />
                {navLink(nav.about)}
                {navLink(nav.contact)}
              </ul>

              <div className="pl-5 border-l border-border shrink-0">
                <LanguageSwitcher />
              </div>

              {/* Both outline: the page's hero owns the one orange button (GUIDELINES.md §4). */}
              <Link
                to={hireCtaPath}
                className={`btn btn-secondary whitespace-nowrap shrink-0 ${currentButton}`}
                aria-current={isCurrentPage(hireCtaPath) ? "page" : undefined}
              >
                {hireCtaText}
              </Link>
              <Link
                to={ctaPath}
                className={`btn btn-secondary whitespace-nowrap shrink-0 ${currentButton}`}
                aria-current={isCurrentPage(ctaPath) ? "page" : undefined}
              >
                {ctaText}
                {arrow}
              </Link>
            </div>

            {/* Mobile Controls */}
            <div className="flex items-center gap-2 xl:hidden">
              <LanguageSwitcher />
              <button
                ref={toggleButtonRef}
                type="button"
                className="inline-flex items-center justify-center -mr-2 transition-colors rounded-md w-11 h-11 text-secondary hover:text-primary"
                onClick={() => (mobileMenuOpen ? close() : openMenu())}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={
                  language === "en"
                    ? mobileMenuOpen ? "Close menu" : "Open menu"
                    : mobileMenuOpen ? "Cerrar menú" : "Abrir menú"
                }
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
            </div>
          </nav>
        </Container>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* The kit's one translucent layer, never blurred. The open menu is the screen, so it and
                the drawer sit above the chat button (z-[9990]) and the cookie bar, and start under
                the 4px metro stripe (z-80) so the stripe stays whole. */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.15 }}
              className="fixed inset-x-0 bottom-0 top-[4px] z-[9992] scrim xl:hidden"
              onClick={close}
              aria-hidden="true"
            />

            {/* Slides in under 200ms, the kit's limit for a panel opening. */}
            <m.div
              ref={panelRef}
              id="mobile-menu"
              initial={reduceMotion ? { opacity: 0 } : { x: "100%" }}
              animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { x: "100%" }}
              transition={reduceMotion ? { duration: 0.12 } : { duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-[4px] bottom-0 right-0 z-[9993] flex flex-col w-full max-w-sm border-l bg-surface-raised border-border shadow-2 xl:hidden"
              role="dialog"
              aria-modal="true"
              aria-label={language === "en" ? "Mobile menu" : "Menú móvil"}
            >
              {/* Scrolling stops at the drawer's ends instead of moving the page; the bottom padding
                  clears the iPhone home bar. */}
              <div className="flex flex-col flex-1 px-6 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain">
                <div className="flex items-center justify-between gap-3">
                  <span className="eyebrow">{language === "en" ? "Menu" : "Menú"}</span>
                  <button
                    ref={closeButtonRef}
                    type="button"
                    className="inline-flex items-center justify-center -mr-2 transition-colors rounded-md w-11 h-11 text-secondary hover:text-primary hover:bg-surface-sunken"
                    onClick={close}
                    aria-label={language === "en" ? "Close menu" : "Cerrar menú"}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6l-12 12" />
                    </svg>
                  </button>
                </div>

                {/* The drawer is the screen while it is open, so its main action is the orange one. */}
                <div className="grid gap-3 mt-5">
                  <Link
                    to={ctaPath}
                    className="w-full btn btn-lg btn-primary"
                    onClick={followLink(ctaPath)}
                    aria-current={isCurrentPage(ctaPath) ? "page" : undefined}
                  >
                    {ctaText}
                    {arrow}
                  </Link>
                  <Link
                    to={hireCtaPath}
                    className={`w-full btn btn-lg btn-secondary ${currentButton}`}
                    onClick={followLink(hireCtaPath)}
                    aria-current={isCurrentPage(hireCtaPath) ? "page" : undefined}
                  >
                    {hireCtaText}
                  </Link>
                </div>

                <nav className="flex-1 mt-8" aria-label={language === "en" ? "Primary" : "Principal"}>
                  {/* A class keeps globals.css's bare-list rule (ul:not([class])) from adding dots. */}
                  <ul className="list-none">
                    <li>
                      <Link
                        to={nav.home.to}
                        className={drawerLinkClass(nav.home.to)}
                        onClick={followLink(nav.home.to)}
                        aria-current={isCurrentPage(nav.home.to) ? "page" : undefined}
                      >
                        {nav.home.label}
                      </Link>
                    </li>

                    <li>
                      <button
                        type="button"
                        className="flex items-center justify-between w-full py-3 text-2xl font-bold tracking-tight text-left transition-colors rounded-md text-primary hover:text-accent-text"
                        onClick={() => setOpenSection(openSection === "services" ? null : "services")}
                        aria-expanded={openSection === "services"}
                        aria-controls="mobile-services-panel"
                      >
                        {nav.services.trigger}
                        {chevron(openSection === "services")}
                      </button>
                      {accordion(
                        "mobile-services-panel",
                        openSection === "services",
                        <>
                          {nav.services.groups.map((group) => (
                            <React.Fragment key={group.heading}>
                              <p className="mt-3 mb-1 eyebrow">{group.heading}</p>
                              {group.links.map((link) => (
                                <Link
                                  key={link.to}
                                  to={link.to}
                                  className={drawerSubLinkClass(link.to)}
                                  onClick={followLink(link.to)}
                                  aria-current={isCurrentPage(link.to) ? "page" : undefined}
                                >
                                  {link.label}
                                </Link>
                              ))}
                            </React.Fragment>
                          ))}
                          <Link
                            to={nav.services.viewAll.to}
                            className="block py-2.5 mt-1 text-base font-semibold rounded-md text-accent-text hover:underline"
                            onClick={followLink(nav.services.viewAll.to)}
                            aria-current={isCurrentPage(nav.services.viewAll.to) ? "page" : undefined}
                          >
                            {nav.services.viewAll.label}
                          </Link>
                        </>,
                      )}
                    </li>

                    <li>
                      <button
                        type="button"
                        className="flex items-center justify-between w-full py-3 text-2xl font-bold tracking-tight text-left transition-colors rounded-md text-primary hover:text-accent-text"
                        onClick={() => setOpenSection(openSection === "industries" ? null : "industries")}
                        aria-expanded={openSection === "industries"}
                        aria-controls="mobile-industries-panel"
                      >
                        {nav.services.industries.heading}
                        {chevron(openSection === "industries")}
                      </button>
                      {accordion(
                        "mobile-industries-panel",
                        openSection === "industries",
                        <>
                          {nav.services.industries.links.map((link) => (
                            <Link
                              key={link.to}
                              to={link.to}
                              className={drawerSubLinkClass(link.to)}
                              onClick={followLink(link.to)}
                              aria-current={isCurrentPage(link.to) ? "page" : undefined}
                            >
                              {link.label}
                            </Link>
                          ))}
                          <Link
                            to={nav.services.industries.viewAll.to}
                            className="block py-2.5 text-base font-semibold rounded-md text-accent-text hover:underline"
                            onClick={followLink(nav.services.industries.viewAll.to)}
                            aria-current={isCurrentPage(nav.services.industries.viewAll.to) ? "page" : undefined}
                          >
                            {nav.services.industries.viewAll.label}
                          </Link>
                        </>,
                      )}
                    </li>

                    <li>
                      <Link
                        to={nav.about.to}
                        className={drawerLinkClass(nav.about.to)}
                        onClick={followLink(nav.about.to)}
                        aria-current={isCurrentPage(nav.about.to) ? "page" : undefined}
                      >
                        {nav.about.label}
                      </Link>
                    </li>

                    <li>
                      <Link
                        to={nav.contact.to}
                        className={drawerLinkClass(nav.contact.to)}
                        onClick={followLink(nav.contact.to)}
                        aria-current={isCurrentPage(nav.contact.to) ? "page" : undefined}
                      >
                        {nav.contact.label}
                      </Link>
                    </li>
                  </ul>
                </nav>

                <div className="pt-6 mt-6 border-t border-border">
                  <p className="mb-3 eyebrow">{language === "en" ? "Direct contact" : "Contacto directo"}</p>
                  <a
                    href={`mailto:${CONTACT.EMAIL}`}
                    className="flex items-center gap-3 px-3 py-2.5 min-h-11 rounded-md text-sm text-secondary hover:text-primary hover:bg-surface-sunken transition-colors"
                  >
                    <svg className="flex-shrink-0 w-4 h-4 text-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>
                    <span>{CONTACT.EMAIL}</span>
                  </a>
                </div>
              </div>
            </m.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
